import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Store, Copy, ThumbsUp, Sparkles, Upload, TrendingUp, Filter, Star, GitFork, Flag, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MarketplacePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [filterIndustry, setFilterIndustry] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [sort, setSort] = useState("featured");
  const [companies, setCompanies] = useState([]);
  const [cloneDialog, setCloneDialog] = useState(null);
  const [forkDialog, setForkDialog] = useState(null);
  const [reportDialog, setReportDialog] = useState(null);
  const [treeDialog, setTreeDialog] = useState(null);
  const [treeData, setTreeData] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [targetCompany, setTargetCompany] = useState("");
  const [forkName, setForkName] = useState("");

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filterIndustry !== "all") params.set("industry", filterIndustry);
      if (filterType !== "all") params.set("type", filterType);
      params.set("sort", sort);
      const res = await axios.get(`${API}/marketplace/templates?${params}`, { withCredentials: true });
      setItems(res.data.items || []);
      setIndustries(res.data.industries || []);
    } catch {
      toast.error("Marketplace konnte nicht geladen werden");
    }
  };

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true }).then((r) => setCompanies(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterIndustry, filterType, sort]);

  const cloneTemplate = async () => {
    if (!cloneDialog || !targetCompany) return;
    try {
      await axios.post(
        `${API}/marketplace/templates/${cloneDialog.id}/clone`,
        { company_id: targetCompany },
        { withCredentials: true }
      );
      toast.success(`Template in ${companies.find((c) => c.id === targetCompany)?.name} kopiert`);
      setCloneDialog(null);
      setTargetCompany("");
      load(); // refresh clone_count
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const upvote = async (id) => {
    try {
      const res = await axios.post(`${API}/marketplace/templates/${id}/upvote`, {}, { withCredentials: true });
      setItems((prev) => prev.map((it) =>
        it.id === id
          ? { ...it, upvote_count: (it.upvote_count || 0) + (res.data.upvoted ? 1 : -1) }
          : it
      ));
    } catch {
      toast.error("Upvote fehlgeschlagen");
    }
  };

  const toggleFeature = async (id) => {
    try {
      const res = await axios.post(`${API}/marketplace/templates/${id}/feature`, {}, { withCredentials: true });
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, is_featured: res.data.is_featured } : it));
      toast.success(res.data.is_featured ? "Als Featured markiert" : "Featured entfernt");
    } catch {
      toast.error("Nur Admins koennen kuratieren");
    }
  };

  const forkTemplate = async () => {
    if (!forkDialog || !targetCompany) return;
    try {
      await axios.post(
        `${API}/marketplace/templates/${forkDialog.id}/fork`,
        { company_id: targetCompany, name: forkName || `${forkDialog.name} (Fork)` },
        { withCredentials: true }
      );
      toast.success(`Fork erstellt und als neue Marketplace-Version veroeffentlicht`);
      setForkDialog(null);
      setForkName("");
      setTargetCompany("");
      load();
    } catch {
      toast.error("Fork fehlgeschlagen");
    }
  };

  const submitReport = async () => {
    if (!reportDialog || !reportReason.trim()) return;
    try {
      await axios.post(
        `${API}/marketplace/templates/${reportDialog.id}/report`,
        { reason: reportReason },
        { withCredentials: true }
      );
      toast.success("Report gesendet — Admin wird pruefen");
      setReportDialog(null);
      setReportReason("");
    } catch {
      toast.error("Report fehlgeschlagen");
    }
  };

  const openTree = async (item) => {
    setTreeDialog(item);
    setTreeData(null);
    try {
      const r = await axios.get(`${API}/marketplace/templates/${item.id}/forks`, { withCredentials: true });
      setTreeData(r.data);
    } catch {
      toast.error("Fork-Tree konnte nicht geladen werden");
    }
  };

  const types = ["all", "post", "reel", "story"];

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <Store className="w-7 h-7 text-[#2563EB]" /> Template Marketplace
          </h1>
          <p className="text-sm text-[var(--studio-text-muted)] mt-1">
            Entdecke bewaehrte Prompt-Templates von anderen Firmen und uebernimm sie mit einem Klick.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg bg-[var(--studio-bg)] border border-[var(--studio-border)]">
        <Filter className="w-4 h-4 text-[var(--studio-text-muted)]" />
        <Select value={filterIndustry} onValueChange={setFilterIndustry}>
          <SelectTrigger className="w-48 bg-white" data-testid="marketplace-industry-filter">
            <SelectValue placeholder="Branche" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Branchen</SelectItem>
            {industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40 bg-white" data-testid="marketplace-type-filter">
            <SelectValue placeholder="Typ" />
          </SelectTrigger>
          <SelectContent>
            {types.map((t) => <SelectItem key={t} value={t}>{t === "all" ? "Alle Typen" : t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-40 bg-white" data-testid="marketplace-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="featured">Featured zuerst</SelectItem>
            <SelectItem value="popular">Beliebt</SelectItem>
            <SelectItem value="new">Neu</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs text-[var(--studio-text-muted)]" data-testid="marketplace-count">
          {items.length} Templates
        </span>
      </div>

      {/* Grid */}
      {items.length === 0 ? (
        <div className="text-center py-16">
          <Sparkles className="w-12 h-12 text-[var(--studio-border)] mx-auto mb-3" />
          <p className="text-[var(--studio-text-muted)]">Noch keine Templates im Marketplace.</p>
          <p className="text-xs text-[var(--studio-text-muted)] mt-1">
            Oeffne einen Prompt-Template, klicke "Veroeffentlichen" und teile ihn mit der Community.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((item) => (
            <Card key={item.id} className="border-[var(--studio-border)] shadow-none hover:shadow-md transition-shadow" data-testid={`marketplace-item-${item.id}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-base">{item.name}</h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {item.industry && (
                        <Badge variant="outline" className="text-[10px] border-[#2563EB]/30 text-[#2563EB]">
                          {item.industry}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-[10px] capitalize">{item.type}</Badge>
                      {item.is_featured && (
                        <Badge className="text-[10px] bg-amber-100 text-amber-800 border-0">Featured</Badge>
                      )}
                    </div>
                  </div>
                </div>

                {item.description && (
                  <p className="text-xs text-[var(--studio-text-muted)] line-clamp-2">{item.description}</p>
                )}

                <div className="p-3 rounded-lg bg-[var(--studio-bg)] text-xs font-mono text-[var(--studio-text)] max-h-24 overflow-hidden relative">
                  {item.template_text?.slice(0, 200) || ""}
                  {(item.template_text?.length || 0) > 200 && (
                    <span className="absolute bottom-0 right-0 left-0 h-8 bg-gradient-to-t from-[var(--studio-bg)] to-transparent" />
                  )}
                </div>

                {item.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {item.tags.map((tag, i) => (
                      <span key={i} className="text-[10px] text-[var(--studio-text-muted)]">#{tag}</span>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t border-[var(--studio-border)]">
                  <div className="flex items-center gap-3 text-xs text-[var(--studio-text-muted)]">
                    <span className="flex items-center gap-1" title="Clone-Count">
                      <TrendingUp className="w-3 h-3" /> {item.clone_count || 0}
                    </span>
                    <button
                      onClick={() => upvote(item.id)}
                      data-testid={`upvote-${item.id}`}
                      className="flex items-center gap-1 hover:text-[#2563EB] transition-colors"
                      title="Upvote"
                    >
                      <ThumbsUp className="w-3 h-3" /> {item.upvote_count || 0}
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => toggleFeature(item.id)}
                        data-testid={`feature-${item.id}`}
                        title={item.is_featured ? "Featured entfernen" : "Als Featured markieren"}
                        className={`flex items-center gap-1 transition-colors ${
                          item.is_featured ? "text-amber-500" : "hover:text-amber-500"
                        }`}
                      >
                        <Star className="w-3 h-3" fill={item.is_featured ? "currentColor" : "none"} />
                      </button>
                    )}
                    {item.forked_from && (
                      <button
                        onClick={() => openTree(item)}
                        className="flex items-center gap-1 text-[10px] opacity-60 hover:opacity-100"
                        data-testid={`tree-${item.id}`}
                        title="Fork-Baum anzeigen"
                      >
                        <GitFork className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      onClick={() => setReportDialog(item)}
                      data-testid={`report-${item.id}`}
                      className="text-[var(--studio-text-muted)] hover:text-red-500 transition-colors"
                      title="Melden"
                    >
                      <Flag className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setForkDialog(item); setForkName(`${item.name} (Fork)`); }}
                      data-testid={`fork-${item.id}`}
                      className="h-8 text-xs"
                      title="Als Fork neu veroeffentlichen"
                    >
                      <GitFork className="w-3 h-3 mr-1" /> Fork
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setCloneDialog(item)}
                      data-testid={`clone-${item.id}`}
                      className="h-8 text-xs bg-[#2563EB] hover:bg-[#1D4ED8]"
                    >
                      <Copy className="w-3 h-3 mr-1" /> Uebernehmen
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Clone Dialog */}
      <Dialog open={!!cloneDialog} onOpenChange={(o) => !o && setCloneDialog(null)}>
        <DialogContent data-testid="clone-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Copy className="w-5 h-5 text-[#2563EB]" />
              Template uebernehmen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">
              In welche Firma soll <strong>{cloneDialog?.name}</strong> kopiert werden?
            </p>
            <Select value={targetCompany} onValueChange={setTargetCompany}>
              <SelectTrigger data-testid="clone-target-select">
                <SelectValue placeholder="Firma waehlen..." />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCloneDialog(null)} data-testid="clone-cancel-btn">
                Abbrechen
              </Button>
              <Button
                onClick={cloneTemplate}
                disabled={!targetCompany}
                data-testid="clone-confirm-btn"
                className="bg-[#2563EB] hover:bg-[#1D4ED8]"
              >
                <Upload className="w-4 h-4 mr-2" /> Kopieren
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Fork Dialog */}
      <Dialog open={!!forkDialog} onOpenChange={(o) => !o && setForkDialog(null)}>
        <DialogContent data-testid="fork-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <GitFork className="w-5 h-5 text-[#2563EB]" />
              Fork veroeffentlichen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[var(--studio-text-muted)]">
              Erstellt eine Kopie in deiner Firma UND eine neue Marketplace-Version mit Attribution zu <strong>{forkDialog?.name}</strong>.
            </p>
            <div>
              <label className="text-xs text-[var(--studio-text-muted)]">Neuer Name</label>
              <input
                value={forkName}
                onChange={(e) => setForkName(e.target.value)}
                data-testid="fork-name-input"
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="z.B. Sommer-Aktion (modernisiert)"
              />
            </div>
            <Select value={targetCompany} onValueChange={setTargetCompany}>
              <SelectTrigger data-testid="fork-target-select">
                <SelectValue placeholder="Deine Firma waehlen..." />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setForkDialog(null)} data-testid="fork-cancel-btn">
                Abbrechen
              </Button>
              <Button
                onClick={forkTemplate}
                disabled={!targetCompany || !forkName}
                data-testid="fork-confirm-btn"
                className="bg-[#2563EB] hover:bg-[#1D4ED8]"
              >
                <GitFork className="w-4 h-4 mr-2" /> Fork erstellen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Report Dialog */}
      <Dialog open={!!reportDialog} onOpenChange={(o) => !o && setReportDialog(null)}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Flag className="w-5 h-5 text-red-500" />
              Template melden
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[var(--studio-text-muted)]">
              Warum moechtest du <strong>{reportDialog?.name}</strong> melden?
            </p>
            <textarea
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              data-testid="report-reason-input"
              className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="z.B. Spam, irrefuehrender Inhalt, Copyright-Verletzung..."
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setReportDialog(null)} data-testid="report-cancel-btn">
                Abbrechen
              </Button>
              <Button
                onClick={submitReport}
                disabled={!reportReason.trim()}
                data-testid="report-submit-btn"
                className="bg-red-500 hover:bg-red-600"
              >
                <Flag className="w-4 h-4 mr-2" /> Senden
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fork Tree Dialog */}
      <Dialog open={!!treeDialog} onOpenChange={(o) => !o && setTreeDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="tree-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <GitFork className="w-5 h-5 text-[#2563EB]" /> Fork-Baum
            </DialogTitle>
          </DialogHeader>
          {!treeData ? (
            <p className="text-sm text-[var(--studio-text-muted)]">Lade...</p>
          ) : (
            <div className="space-y-1 text-sm">
              {treeData.ancestry.map((a, i) => (
                <div
                  key={a.id}
                  className="flex items-center gap-2 p-2 rounded bg-[var(--studio-bg)]"
                  style={{ marginLeft: `${i * 16}px` }}
                  data-testid={`tree-ancestor-${a.id}`}
                >
                  <ChevronRight className="w-3 h-3 text-[var(--studio-text-muted)]" />
                  <span className="text-xs text-[var(--studio-text-muted)]">
                    {a.name} &middot; {a.clone_count || 0}&times; geklont
                  </span>
                </div>
              ))}
              <div
                className="flex items-center gap-2 p-2 rounded bg-[#2563EB]/10 border border-[#2563EB]/30 font-semibold"
                style={{ marginLeft: `${treeData.ancestry.length * 16}px` }}
                data-testid="tree-current"
              >
                <ChevronRight className="w-3 h-3 text-[#2563EB]" />
                <span className="text-sm">{treeData.current.name}</span>
                <span className="text-xs text-[var(--studio-text-muted)] ml-auto">aktuell</span>
              </div>
              {treeData.children.length > 0 && (
                <div className="pt-2 mt-2 border-t border-[var(--studio-border)]">
                  <p className="text-xs text-[var(--studio-text-muted)] mb-1">Nachfolger ({treeData.children.length}):</p>
                  {treeData.children.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center gap-2 p-2 rounded bg-[var(--studio-bg)]"
                      style={{ marginLeft: `${(treeData.ancestry.length + 1) * 16}px` }}
                      data-testid={`tree-child-${c.id}`}
                    >
                      <GitFork className="w-3 h-3 text-[var(--studio-text-muted)]" />
                      <span className="text-xs">{c.name}</span>
                      <span className="text-[10px] text-[var(--studio-text-muted)] ml-auto">
                        {c.clone_count || 0}&times; &middot; {c.upvote_count || 0}&hearts;
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
